/**
 * Splash → auth check → route (feature #1). This is the index route. It waits
 * for the auth phase to settle, then redirects to the correct destination.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useAppConfig } from '../../hooks/useAppConfig';
import { ROUTES } from '../../routes/paths';

export function SplashScreen(): JSX.Element {
  const { phase, status } = useAuth();
  const config = useAppConfig();
  const approvalRequired = config?.approvalRequired ?? true;
  const navigate = useNavigate();

  useEffect(() => {
    if (phase === 'loading') return;
    if (phase === 'signedOut') {
      navigate(ROUTES.login, { replace: true });
      return;
    }
    // Signed in via OTP but no profile doc yet → collect it (post-OTP register).
    if (phase === 'needsRegistration') {
      navigate(ROUTES.register, { replace: true });
      return;
    }
    switch (status) {
      case 'approved':
        navigate(ROUTES.home, { replace: true });
        break;
      case 'blocked':
      case 'rejected':
        navigate(ROUTES.blocked, { replace: true });
        break;
      case 'pending':
      case 'unknown':
      default:
        // Approval toggle OFF → pending users go straight to Home.
        navigate(approvalRequired ? ROUTES.underReview : ROUTES.home, { replace: true });
        break;
    }
  }, [phase, status, approvalRequired, navigate]);

  // Brand splash: the logo while auth/config resolve (the native splash hands
  // over to this, so the transition is seamless).
  return (
    <div className="flex min-h-full flex-1 flex-col items-center justify-center gap-6 bg-ground px-10">
      <img
        src="/logo-full.png"
        alt="ANYWAY IT SOLUTIONS"
        className="w-full max-w-[260px]"
      />
      <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-transparent" />
    </div>
  );
}
